import { create } from "zustand";

import { apiFetch, setUnauthorizedHandler } from "@/api/client";
import type { LoginResponse, UserPayload } from "@/api/schemas";

type Status = "loading" | "authenticated" | "unauthenticated";

interface AuthStore {
  user: UserPayload | null;
  status: Status;
  fetchMe: () => Promise<UserPayload | null>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuth = create<AuthStore>((set) => ({
  user: null,
  status: "loading",

  fetchMe: async () => {
    try {
      const user = await apiFetch<UserPayload>("/api/auth/me");
      set({ user, status: "authenticated" });
      return user;
    } catch {
      set({ user: null, status: "unauthenticated" });
      return null;
    }
  },

  login: async (email, password) => {
    const res = await apiFetch<LoginResponse>("/api/auth/login", {
      method: "POST",
      json: { email, password },
    });
    set({ user: res.user, status: "authenticated" });
  },

  logout: async () => {
    try {
      await apiFetch<void>("/api/auth/logout", { method: "POST" });
    } finally {
      set({ user: null, status: "unauthenticated" });
    }
  },
}));

// When apiFetch detects an unrecoverable 401 (refresh failed or retry still
// 401'd), flip the store to `unauthenticated` so the `_auth` route guard
// redirects to /login on the next navigation. Registered once at module load.
setUnauthorizedHandler(() => {
  if (useAuth.getState().status !== "unauthenticated") {
    useAuth.setState({ user: null, status: "unauthenticated" });
  }
});
